import { useState, useEffect } from 'react';
import { todayFeatured, type Category } from '../../../mocks/categories';
import { openWhatsApp } from '../../../utils/whatsapp';
import { trackEvent } from '../../../utils/tracking';
import InlineCitySelector from './InlineCitySelector';

function useCountdown(targetHour = 23) {
  const [timeLeft, setTimeLeft] = useState({ h: '00', m: '00', s: '00' });
  useEffect(() => {
    const calc = () => {
      const now = new Date();
      const end = new Date();
      end.setHours(targetHour, 59, 59, 0);
      if (end <= now) end.setDate(end.getDate() + 1);
      const diff = Math.max(0, Math.floor((end.getTime() - now.getTime()) / 1000));
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setTimeLeft({ h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0'), s: String(s).padStart(2, '0') });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetHour]);
  return timeLeft;
}

interface TodayCardProps {
  cat: Category;
}

function TodayCard({ cat }: TodayCardProps) {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSend = (city: string) => {
    trackEvent('category_click', { category: cat.name, city, section: 'today_featured' });
    trackEvent('whatsapp_redirect', { category: cat.name, city });
    openWhatsApp(cat.name, city);
  };

  return (
    <div
      className="group relative rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background: `linear-gradient(160deg, ${cat.gradientFrom}18, ${cat.gradientTo}10)`,
        border: `1px solid ${cat.gradientFrom}30`,
        boxShadow: `0 8px 32px ${cat.glowColor}15`,
      }}
    >
      {/* Animated top border */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5 opacity-80"
        style={{ background: `linear-gradient(90deg, ${cat.gradientFrom}, ${cat.gradientTo})` }}
      />

      {/* Clickable main area */}
      <div
        className="p-5 cursor-pointer"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
        aria-label={`${cat.name} için şehir seç`}
      >
        <div
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium mb-4"
          style={{ background: `${cat.gradientFrom}20`, color: cat.gradientFrom, border: `1px solid ${cat.gradientFrom}35` }}
        >
          <span>⚡</span> Bugün Aktif
        </div>

        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
          style={{ background: `linear-gradient(135deg, ${cat.gradientFrom}30, ${cat.gradientTo}20)` }}
        >
          <i className={`${cat.icon} text-2xl`} style={{ color: cat.gradientFrom }} />
        </div>

        <h4
          className="text-white font-bold text-base mb-1"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {cat.name}
        </h4>
        <p className="text-gray-500 text-xs mb-4 leading-relaxed">{cat.description}</p>

        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: cat.gradientFrom }}>
          <i className="ri-map-pin-line text-sm" />
          Şehir Seç &amp; Kampanya Al
          <i className="ri-arrow-right-line text-xs" />
        </div>
      </div>

      {open && (
        <InlineCitySelector
          accentColor={cat.gradientFrom}
          targetName={cat.name}
          onClose={handleClose}
          onSend={handleSend}
        />
      )}
    </div>
  );
}

export default function TodayOpportunitiesSection() {
  const { h, m, s } = useCountdown();

  return (
    <section className="bg-[#0a0a0f] py-20 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-10 gap-5">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">⚡</span>
              <h2
                className="text-white font-bold text-2xl md:text-3xl"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Bugünün Fırsat Alanları
              </h2>
            </div>
            <p className="text-gray-500 text-sm">Her gün güncellenen özel fırsat kategorileri</p>
          </div>

          <div
            className="flex items-center gap-3 px-5 py-3 rounded-2xl"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
          >
            <i className="ri-time-line text-amber-400 text-lg" />
            <span className="text-gray-400 text-sm">Bugün bitiyor:</span>
            <div className="flex items-center gap-1">
              {[h, m, s].map((val, i) => (
                <span key={i}>
                  <span className="text-amber-400 font-bold text-base font-mono">{val}</span>
                  {i < 2 && <span className="text-amber-500 font-bold mx-0.5">:</span>}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {todayFeatured.map((cat) => (
            <TodayCard key={cat.id} cat={cat} />
          ))}
        </div>
      </div>
    </section>
  );
}
